import { getValidChildren } from '@chakra-ui/react-utils';
import {
  chakra,
  type BreadcrumbProps as ChakraBreadcrumbProps,
  useMultiStyleConfig,
  omitThemingProps,
  BreadcrumbItemProps,
} from '@chakra-ui/react-v2';
import { cx } from '@chakra-ui/utils';
import { cloneElement, forwardRef, useCallback, useRef } from 'react';
import { Merge } from 'type-fest';

import {
  Box,
  EllipsisHorizontalIcon,
  MenuButton,
  Menu,
  MenuList,
  Portal,
  IconButton,
  IconButtonProps,
  BreadcrumbItem,
} from '@frontend/legacy-components';
import {
  useVisibleTruncatedChildren,
  type UseVisibleTruncatedChildrenRenderOverflowElementParam,
} from '@frontend/utils';

import { BreadcrumbStylesProvider } from './context';

type BreadcrumbRenderSeparatorParam = {
  key: string | null;
  totalItemCount: number;
  itemIndex: number;
};

export type BreadcrumbSeparatorRenderFn = (
  props: BreadcrumbRenderSeparatorParam,
) => React.ReactElement;

export type BreadcrumbProps = Merge<
  ChakraBreadcrumbProps,
  {
    itemMinWidth?: BreadcrumbItemProps['minWidth'];
    separator?:
      | ChakraBreadcrumbProps['separator']
      | BreadcrumbSeparatorRenderFn;
  }
>;

const MIN_VISIBLE_ITEMS = 1;
const MAX_VISIBLE_ITEMS = 6;

const CustomMenuButton = forwardRef<'button', IconButtonProps>(
  function CustomMenuButton(props, ref) {
    return (
      <IconButton ref={ref} {...props}>
        <EllipsisHorizontalIcon boxSize={2.25} />
      </IconButton>
    );
  },
);

const INDICES_TO_SHOW = [0];
const OVERFLOW_ELEMENT_POSITION = 1;

export const Breadcrumb = forwardRef<'nav', BreadcrumbProps>(
  function Breadcrumb(props, ref) {
    const ownProps = omitThemingProps(props);

    const {
      children,
      spacing = '0.5rem',
      separator: separatorProp,
      className,
      listProps,
      itemMinWidth = 4.5,
      ...restProps
    } = ownProps;

    const styles = useMultiStyleConfig('Breadcrumb');
    const _className = cx('breadcrumb', className);

    const listRef = useRef<HTMLOListElement>(null);

    const renderOverflowElement = useCallback(
      ({
        visibleItemCount,
        childArray,
      }: UseVisibleTruncatedChildrenRenderOverflowElementParam) => {
        const hiddenItemCount = childArray.length - visibleItemCount;
        const hiddenItems = childArray.slice(
          OVERFLOW_ELEMENT_POSITION,
          OVERFLOW_ELEMENT_POSITION + hiddenItemCount,
        );
        return (
          <BreadcrumbItem key="overflow">
            <Menu isLazy={true}>
              <MenuButton as={CustomMenuButton} size="sm" />
              <Portal>
                <MenuList maxWidth="20rem">
                  {hiddenItems.toReversed().map((child) => {
                    return (
                      <Box key={child.key} px={2} py={0.5}>
                        {child.props.children}
                      </Box>
                    );
                  })}
                </MenuList>
              </Portal>
            </Menu>
          </BreadcrumbItem>
        );
      },
      [],
    );

    const { truncatedChildren, visibleItemCount } = useVisibleTruncatedChildren(
      {
        children,
        indicesToShow: INDICES_TO_SHOW,
        renderOverflowElement,
        overflowElementPosition: OVERFLOW_ELEMENT_POSITION,
        containerRef: listRef,
        minVisibleItems: MIN_VISIBLE_ITEMS,
        maxVisibleItems: MAX_VISIBLE_ITEMS,
        forceLastItemVisibility: true,
      },
    );

    const validChildren = getValidChildren(truncatedChildren);
    const count = validChildren.length;

    const clones = validChildren.map((child, index) => {
      const separator =
        typeof separatorProp === 'function'
          ? separatorProp({
              key: child.key,
              totalItemCount: count,
              itemIndex: index,
            })
          : separatorProp ?? '/';
      return cloneElement(child, {
        separator,
        spacing,
        minWidth:
          count - 2 === index && visibleItemCount > 2
            ? itemMinWidth
            : undefined,
        overflow:
          (count === index + 1 && visibleItemCount === 2) ||
          (count - 2 && visibleItemCount > 2) === index
            ? 'hidden'
            : undefined,
        isLastChild: count === index + 1,
      });
    });

    return (
      <chakra.nav
        __css={styles.container}
        className={_className}
        ref={ref}
        {...restProps}
      >
        <BreadcrumbStylesProvider value={styles}>
          <chakra.ol __css={styles.list} ref={listRef}>
            {clones}
          </chakra.ol>
        </BreadcrumbStylesProvider>
      </chakra.nav>
    );
  },
);
Breadcrumb.displayName = 'Breadcrumb';
